import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useAuth } from "../components/Auth";
import { useFeedback } from "../components/Feedback";
import { Card, ErrorBox, Facts, Loading, PageHeader } from "../components/Page";
import { api } from "../lib/api";
import { day, monthLabel, num, usd, when } from "../lib/format";
import { useApi } from "../lib/useApi";

interface PnlRow { label: string; values: (number | null)[]; kind: "account" | "summary"; depth: number; group?: string }
interface Status {
  configured: boolean; environment: string | null; connected: boolean; company: string | null;
  connected_at: string | null; connected_by: string | null; refresh_expires_at: string | null;
  as_of: string | null; last_check_error: string | null;
  months: { month: string; books_usd: number | null; app_usd: number | null; difference: number | null }[];
  pnl: { columns: string[]; rows: PnlRow[] } | null;
  open_invoices: { doc_number: string | null; customer: string | null; txn_date: string | null; due_date: string | null;
                   currency: string; total: number | null; balance: number | null; days_late: number | null }[];
  aging?: { bucket: string; balance: number }[]; open_total?: number;
}

const RETURNED: Record<string, string> = {
  declined: "QuickBooks was not connected: the request was declined on Intuit's page.",
  state: "That return from Intuit did not match a Connect click from here, so it was ignored. Try Connect again.",
  admin: "Only an admin can connect QuickBooks.",
  token: "Intuit did not hand over the connection. Try Connect again.",
  intuit: "Intuit reported a problem. Try Connect again.",
};
// The P&L summary lines worth a row on this page; the rest is in QuickBooks.
const PNL_LINES = ["Income", "GrossProfit", "Expenses", "NetOperatingIncome", "NetIncome", "COGS"];

export function QuickBooks() {
  const { data: d, error, reload } = useApi<Status>("/quickbooks");
  const { user } = useAuth();
  const { toast, confirm } = useFeedback();
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const returned = params.get("error");
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!d) return <Loading />;
  const admin = user?.role === "admin";

  const act = async (fn: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    try { await fn(); if (done) toast(done); reload(); } catch (e) { toast((e as Error).message, "error"); } finally { setBusy(false); }
  };
  const connect = () => act(async () => {
    const r = await api.post<{ url: string }>("/quickbooks/connect");
    window.location.assign(r.url);
  });

  const header = (
    <PageHeader title="QuickBooks"
      sub="The books, read only: invoices, sales receipts, credit memos and the profit and loss, set against the sales the app already has."
      actions={d.connected ? <>
        <button className="btn btn-primary" disabled={busy} onClick={() => act(() => api.post("/quickbooks/sync"), "QuickBooks read again")}>
          {busy ? "Reading…" : "Read QuickBooks now"}</button>
        {admin && <button className="btn" disabled={busy} onClick={async () => {
          if (await confirm({ title: "Disconnect QuickBooks?", body: "The app stops reading the books and gives the connection back to Intuit. Nothing in QuickBooks changes.", confirm: "Disconnect", danger: true }))
            void act(() => api.post("/quickbooks/disconnect"), "Disconnected");
        }}>Disconnect</button>}
      </> : null} />
  );

  if (!d.connected) {
    return (
      <>
        {header}
        {returned && <p className="integration-error" role="alert">{RETURNED[returned] ?? RETURNED.intuit}</p>}
        <Card title="Connect to QuickBooks">
          <p>Connecting takes a minute and no password is shared with this app. You sign in on Intuit's own page, pick the
            company, and approve. The app then reads, every morning:</p>
          <ul>
            <li>invoices, sales receipts and credit memos from the start of last year;</li>
            <li>the profit and loss, month by month;</li>
            <li>what is still owed on each invoice.</li>
          </ul>
          <p>It never writes to QuickBooks: there is no code here that could, and a test keeps it that way. Disconnect any
            time, here or in QuickBooks.</p>
          {!d.configured ? (
            <p className="note muted" data-testid="qbo-not-configured">Waiting on our Intuit developer app, which has to exist before
              the button can work. Nothing for you to do yet.</p>
          ) : admin ? (
            <button className="btn btn-primary" disabled={busy} onClick={connect} data-testid="qbo-connect">Connect to QuickBooks</button>
          ) : (
            <p className="note muted">An admin connects QuickBooks.</p>
          )}
        </Card>
      </>
    );
  }

  const pnl = d.pnl;
  const pnlRows = pnl ? pnl.rows.filter((r) => r.kind === "summary" && r.group && PNL_LINES.includes(r.group)) : [];
  return (
    <>
      {header}
      {params.get("connected") && <p className="integration-note" role="status">Connected to {d.company ?? "QuickBooks"}.</p>}
      {d.last_check_error && <p className="integration-error" role="alert">Last read failed: {d.last_check_error}</p>}
      <Card>
        <Facts items={[["Company", d.company], ["Read as of", d.as_of ? day(d.as_of) : "not yet"],
          ["Connected", d.connected_at ? `${when(d.connected_at)}${d.connected_by ? ` by ${d.connected_by}` : ""}` : null],
          ["Connection good until", d.refresh_expires_at ? day(d.refresh_expires_at) : null],
          ...(d.environment === "sandbox" ? [["Mode", "Intuit's sandbox (test company)"] as [string, string]] : [])]} />
      </Card>

      {d.months.length > 0 && (
        <Card title="The books against the app, month by month">
          <div className="table-scroll">
            <table className="grid compact" data-testid="qbo-months">
              <thead><tr><th>Month</th><th className="n">QuickBooks</th><th className="n">The app's sales</th><th className="n">Difference</th></tr></thead>
              <tbody>{d.months.map((m) => (
                <tr key={m.month}><td>{monthLabel(m.month)}</td><td className="n">{usd(m.books_usd)}</td><td className="n">{usd(m.app_usd)}</td>
                  <td className={`n${m.difference && Math.abs(m.difference) >= 1 ? " down" : ""}`}>{m.difference == null ? "" : usd(m.difference)}</td></tr>
              ))}</tbody>
            </table>
          </div>
          <p className="note muted">QuickBooks: invoices and sales receipts less credit memos, by their date, in dollars. The app: Zach's
            sales report and MHW's US figures. A difference is a question to ask (an invoice in one and not the other, or a different
            month), not an error in either.</p>
        </Card>
      )}

      {pnl && pnlRows.length > 0 && (
        <Card title="Profit and loss by month">
          <div className="table-scroll">
            <table className="grid compact" data-testid="qbo-pnl">
              <thead><tr><th />{pnl.columns.map((c) => <th key={c} className="n">{c}</th>)}</tr></thead>
              <tbody>{pnlRows.map((r) => (
                <tr key={r.label} className={r.group === "NetIncome" ? "totals" : undefined}><td className="name">{r.label}</td>
                  {r.values.map((v, i) => <td key={i} className="n">{v == null ? "" : usd(v)}</td>)}</tr>
              ))}</tbody>
            </table>
          </div>
        </Card>
      )}

      <Card title={`Still owed${d.open_total ? `: ${usd(d.open_total)}` : ""}`}>
        {d.aging && d.aging.length > 0 && (
          <p className="note">{d.aging.map((a) => `${a.bucket} ${usd(a.balance)}`).join(" · ")}</p>
        )}
        {d.open_invoices.length ? (
          <div className="table-scroll">
            <table className="grid compact" data-testid="qbo-open">
              <thead><tr><th>Invoice</th><th>Customer</th><th>Date</th><th>Due</th><th className="n">Total</th><th className="n">Owed</th><th className="n">Days late</th></tr></thead>
              <tbody>{d.open_invoices.map((i) => (
                <tr key={`${i.doc_number}-${i.customer}`}><td>{i.doc_number ?? ""}</td><td>{i.customer ?? ""}</td><td>{day(i.txn_date)}</td>
                  <td>{day(i.due_date)}</td><td className="n">{usd(i.total)}</td><td className="n">{usd(i.balance)}</td>
                  <td className={`n${i.days_late ? " down" : ""}`}>{i.days_late ? num(i.days_late) : ""}</td></tr>
              ))}</tbody>
            </table>
          </div>
        ) : <p className="note muted">Nothing owed on any invoice.</p>}
      </Card>
    </>
  );
}
